/**
 * @file API service — a1Service.
 * A1 Service
 * All API calls related to A1 stage
 */

import axiosClient from "../api/axiosClient";
import { API_URLS } from "../constants/apiUrls";
import {
  ExtractionProcessOption,
  FormDataResponse,
} from "../interfaces/formInterfaces";

/** Type definition — Fuel Density Api Row. */
export interface FuelDensityApiRow {
  id: number;
  fuelName: string;
  densityKgM3: string;
  densityKgL: string;
  sortOrder?: number;
}

/**
 * Get form data for A1 stage
 */
export const getA1FormData = async (
  stageCode: string,
  options?: { materialId?: number | string }
): Promise<{
  success: boolean;
  data: FormDataResponse | null;
  error?: string;
}> => {
  try {
    const params =
      options?.materialId != null && String(options.materialId).trim() !== ""
        ? { materialId: options.materialId }
        : undefined;
    const response = await axiosClient.get(API_URLS.FORMS_STAGE(stageCode), {
      params,
    });
    return {
      success: true,
      data: response.data.data,
    };
  } catch (error: any) {
    return {
      success: false,
      error: error.response?.data?.message || "Failed to fetch form data",
      data: null,
    };
  }
};

/**
 * List extraction processes for Background finished material (materials.id).
 */
/**
 * Reference list of fuel densities for A1 (fuel mix → density auto-fill).
 */
export const getFuelDensities = async (): Promise<FuelDensityApiRow[]> => {
  const response = await axiosClient.get(API_URLS.FUEL_DENSITIES);
  const data = response.data?.data;
  return Array.isArray(data) ? data : [];
};

/** Exported const — get Extraction Processes By Material. */
export const getExtractionProcessesByMaterial = async (
  materialId: number
): Promise<ExtractionProcessOption[]> => {
  const response = await axiosClient.get(API_URLS.EXTRACTION_PROCESSES, {
    params: { materialId },
  });
  const payload = response.data?.data as
    | { processes?: ExtractionProcessOption[] }
    | undefined;
  const processes = payload?.processes;
  return Array.isArray(processes) ? processes : [];
};

/**
 * Get submission by ID
 */
export const getSubmission = async (
  submissionId: string
): Promise<{
  success: boolean;
  data: any | null;
  error?: string;
}> => {
  try {
    const response = await axiosClient.get(API_URLS.SUBMISSIONS.BY_ID(submissionId));
    return {
      success: true,
      data: response.data.data,
    };
  } catch (error: any) {
    return {
      success: false,
      error: error.response?.data?.message || "Failed to fetch submission",
      data: null,
    };
  }
};

/**
 * Save A1 stage data
 */
export const saveA1Details = async (
  submissionId: string,
  payload: any
): Promise<{
  success: boolean;
  message?: string;
  error?: string;
}> => {
  try {
    const response = await axiosClient.put(
      API_URLS.SUBMISSIONS.A1(submissionId),
      payload
    );
    return {
      success: true,
      message: response.data?.message || "A1 data saved successfully",
    };
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      "Failed to save A1 data";
    return {
      success: false,
      error: errorMessage,
    };
  }
};

